import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import dayjs from 'dayjs';
import {useStoreContext} from '@store/store-context';
import {useWorkspaceUseCase} from '@hooks/workspace-usecase';
import {UserAPIRequestImpl} from '@api/user-api/user-api-request';
import {groupRequestImpl} from '@pages/dashboard/admin/admin-components/admin-people/admin-group/admin-group-request';
import {AnalyticsRequestImpl} from '@pages/dashboard/reporting/reporting-pages/admin-analytics/admin-analytics.request';
import {FeedbackRequestImpl} from '@pages/feedback/feedback-requests';
import {CompanyCheckinsRequestImpl} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.request';
import {
  activeWorkspaceUsers,
  buildFeedbackSignals,
  buildGoalSignals,
  buildManagerDashboardModel,
  buildProgramSignals,
  managerCanUseGroup,
  membersForScope,
  normalizeList,
  referenceId,
  userBelongsToGroup,
} from './manager-dashboard.utils';
import {
  DashboardPeriod,
  DashboardScope,
  FeatureAvailability,
} from './manager-dashboard.types';

const calendarQuarter = (year: number, quarter: number): DashboardPeriod => {
  const starts = dayjs(new Date(year, (quarter - 1) * 3, 1));
  return {
    id: `${year}-Q${quarter}`,
    label: `Q${quarter} ${year}`,
    starts: starts.format('MM/DD/YYYY'),
    ends: starts.add(2, 'month').endOf('month').format('MM/DD/YYYY'),
    year,
    quarter,
  };
};

const customPeriodFromValue = (value: string | null): DashboardPeriod | null => {
  if (!value?.startsWith('custom:')) return null;

  const [starts, ends] = value.slice('custom:'.length).split(':');
  const startDate = dayjs(starts);
  const endDate = dayjs(ends);
  if (!starts || !ends || !startDate.isValid() || !endDate.isValid()) {
    return null;
  }
  if (endDate.isBefore(startDate, 'day')) return null;

  return {
    id: value,
    label: 'Custom',
    starts: startDate.format('MM/DD/YYYY'),
    ends: endDate.format('MM/DD/YYYY'),
    year: startDate.year(),
    quarter: 'custom',
  };
};

export const useManagerDashboard = () => {
  const {
    authStore: {auth},
  } = useStoreContext();
  const {
    isCheckinsEnabled,
    isFeedBackEnabled,
    isGoalEnabled,
  } = useWorkspaceUseCase();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const workspaceId = auth.user.workspace.id;
  const currentUserId = auth.user.id;
  const isAdmin = String(auth.user.role).toLowerCase() === 'admin';
  const userRequest = new UserAPIRequestImpl();
  const groupRequest = new groupRequestImpl();
  const analyticsRequest = new AnalyticsRequestImpl();
  const feedbackRequest = new FeedbackRequestImpl();
  const checkinsRequest = new CompanyCheckinsRequestImpl();

  const usersQuery = useQuery(
    ['manager-dashboard', workspaceId, 'users'],
    () => userRequest.fetchAllUsers(),
    {staleTime: 5 * 60 * 1000},
  );
  const groupsQuery = useQuery(
    ['manager-dashboard', workspaceId, 'groups'],
    () => groupRequest.fetchGroups(),
    {staleTime: 5 * 60 * 1000},
  );
  const groupMembershipsQuery = useQuery(
    ['manager-dashboard', workspaceId, 'group-memberships'],
    () => groupRequest.fetchGroupsMembers(),
    {staleTime: 5 * 60 * 1000},
  );

  const groupMemberships = useMemo(
    () => normalizeList(groupMembershipsQuery.data),
    [groupMembershipsQuery.data],
  );
  const users = useMemo(() => {
    const activeUsers = activeWorkspaceUsers(usersQuery.data);
    return activeUsers.map((user) => ({
      ...user,
      groups: [
        ...(Array.isArray(user?.groups) ? user.groups : []),
        ...groupMemberships.filter(
          (membership) => referenceId(membership?.user) === referenceId(user),
        ),
      ],
    }));
  }, [groupMemberships, usersQuery.data]);
  const groups = useMemo(() => normalizeList(groupsQuery.data), [
    groupsQuery.data,
  ]);

  const scopes = useMemo<DashboardScope[]>(() => {
    const directReportCount = users.filter(
      (user) => referenceId(user?.reviewer) === currentUserId,
    ).length;
    const options: DashboardScope[] = [
      {
        type: 'direct',
        id: 'direct',
        label: 'Direct Reports',
        count: directReportCount,
      },
    ];
    groups
      .filter((group) => managerCanUseGroup(group, auth.user, isAdmin))
      .sort((a, b) =>
        String(a?.name || '').localeCompare(String(b?.name || '')),
      )
      .forEach((group) =>
        options.push({
          type: 'group',
          id: `group:${referenceId(group)}`,
          label: group?.name || 'Unnamed group',
          count: users.filter((user) => userBelongsToGroup(user, group)).length,
        }),
      );
    if (isAdmin) {
      options.push({
        type: 'workspace',
        id: 'workspace',
        label: 'Entire Workspace',
        count: users.filter((user) => referenceId(user) !== currentUserId)
          .length,
      });
    }
    return options;
  }, [auth.user, currentUserId, groups, isAdmin, users]);

  const requestedScope = searchParams.get('scope') || 'direct';
  const scope =
    scopes.find((option) => option.id === requestedScope) || scopes[0];

  const periodOptions = useMemo<DashboardPeriod[]>(() => {
    const currentYear = dayjs().year();
    return [
      currentYear + 1,
      currentYear,
      currentYear - 1,
      currentYear - 2,
    ].flatMap((year) =>
      [4, 3, 2, 1].map((quarter) => calendarQuarter(year, quarter)),
    );
  }, []);

  const defaultPeriod = useMemo<DashboardPeriod>(() => {
    const now = dayjs();
    return calendarQuarter(now.year(), Math.floor(now.month() / 3) + 1);
  }, []);
  const requestedPeriod = searchParams.get('period');
  const period =
    customPeriodFromValue(requestedPeriod) ||
    periodOptions.find(
      (option) =>
        option.id === requestedPeriod ||
        `${option.starts}:${option.ends}` === requestedPeriod,
    ) || defaultPeriod;

  const members = useMemo(() => {
    const scopedMembers = membersForScope({
      users,
      groups,
      currentUser: auth.user,
      scope: scope.id,
    });
    return scopedMembers.map((member) => ({
      ...member,
      groups: groups
        .filter((group) => userBelongsToGroup(member.raw, group))
        .map((group) => group?.name)
        .filter((name): name is string => Boolean(name)),
    }));
  }, [auth.user, groups, scope.id, users]);
  const memberIds = useMemo(() => members.map((member) => member.id), [
    members,
  ]);
  const memberFilter = memberIds.join(',');
  const rosterReady =
    usersQuery.isSuccess &&
    groupsQuery.isSuccess &&
    groupMembershipsQuery.isSuccess;
  const hasMembers = rosterReady && memberIds.length > 0;
  const previousPeriod = useMemo(() => {
    const starts = dayjs(period.starts);
    const ends = dayjs(period.ends);
    const length = Math.max(1, ends.diff(starts, 'day') + 1);
    const previousEnd = starts.subtract(1, 'day');
    return {
      starts: previousEnd.subtract(length - 1, 'day').format('MM/DD/YYYY'),
      ends: previousEnd.format('MM/DD/YYYY'),
    };
  }, [period.ends, period.starts]);

  const goalsQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'goals',
      scope.id,
      memberFilter,
      period.starts,
      period.ends,
    ],
    () =>
      analyticsRequest.fetchSpecifiedGoals(
        {
          startDate: period.starts,
          endDate: period.ends,
          member: memberFilter,
          removeKpiFilter: false,
          isKpi: false,
        },
        undefined,
        true,
      ),
    {enabled: isGoalEnabled && hasMembers, retry: 1},
  );

  const previousGoalsQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'goals-previous',
      scope.id,
      memberFilter,
      previousPeriod.starts,
      previousPeriod.ends,
    ],
    () =>
      analyticsRequest.fetchSpecifiedGoals(
        {
          startDate: previousPeriod.starts,
          endDate: previousPeriod.ends,
          member: memberFilter,
          removeKpiFilter: false,
          isKpi: false,
        },
        undefined,
        true,
      ),
    {enabled: isGoalEnabled && hasMembers, retry: 1},
  );

  const feedbackQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'feedback',
      scope.id,
      memberFilter,
      period.starts,
      period.ends,
    ],
    () =>
      feedbackRequest.fetchFeedbackReporting({
        startDate: period.starts,
        endDate: period.ends,
        user: memberFilter,
      }),
    {enabled: isFeedBackEnabled && hasMembers, retry: 1},
  );

  const feedbackRequestsQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'feedback-requests',
      currentUserId,
      period.starts,
      period.ends,
    ],
    () =>
      feedbackRequest.fetchFeedbackRequests({
        reviewer: currentUserId,
        startDate: period.starts,
        endDate: period.ends,
        include: 'user',
      }),
    {enabled: isFeedBackEnabled && hasMembers, retry: 1},
  );

  const praiseQuery = useQuery(
    ['manager-dashboard', workspaceId, 'praise', period.starts, period.ends],
    () =>
      feedbackRequest.fetchPublicFeedback({
        startDate: period.starts,
        endDate: period.ends,
        paginate: false,
      }),
    {enabled: isFeedBackEnabled && hasMembers, retry: 1},
  );

  const previousFeedbackQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'feedback-previous',
      scope.id,
      memberFilter,
      previousPeriod.starts,
      previousPeriod.ends,
    ],
    () =>
      feedbackRequest.fetchFeedbackReporting({
        startDate: previousPeriod.starts,
        endDate: previousPeriod.ends,
        user: memberFilter,
      }),
    {enabled: isFeedBackEnabled && hasMembers, retry: 1},
  );

  const previousPraiseQuery = useQuery(
    [
      'manager-dashboard',
      workspaceId,
      'praise-previous',
      previousPeriod.starts,
      previousPeriod.ends,
    ],
    () =>
      feedbackRequest.fetchPublicFeedback({
        startDate: previousPeriod.starts,
        endDate: previousPeriod.ends,
        paginate: false,
      }),
    {enabled: isFeedBackEnabled && hasMembers, retry: 1},
  );

  const checkinsQuery = useQuery(
    ['manager-dashboard', workspaceId, 'checkins', scope.id, memberFilter],
    () =>
      checkinsRequest.getListOfCheckins({
        ...(!isAdmin ? {manager: currentUserId} : {}),
        include: ['user', 'cycle'],
        paginate: false,
      }),
    {enabled: isCheckinsEnabled && hasMembers, retry: 1},
  );

  const cyclesQuery = useQuery(
    ['manager-dashboard', workspaceId, 'cycles'],
    () => checkinsRequest.getCheckinCycles(),
    {enabled: isCheckinsEnabled && hasMembers && isAdmin, retry: 1},
  );

  const goals = useMemo(() => buildGoalSignals(goalsQuery.data, members), [
    goalsQuery.data,
    members,
  ]);
  const previousGoals = useMemo(
    () => buildGoalSignals(previousGoalsQuery.data, members),
    [members, previousGoalsQuery.data],
  );
  const feedback = useMemo(() => {
    if (
      !feedbackQuery.isSuccess ||
      !feedbackRequestsQuery.isSuccess ||
      !praiseQuery.isSuccess
    ) {
      return [];
    }
    return buildFeedbackSignals({
      response: feedbackQuery.data,
      requests: feedbackRequestsQuery.data,
      publicPraise: praiseQuery.data,
      members,
      periodStart: period.starts,
    });
  }, [
    feedbackQuery.data,
    feedbackQuery.isSuccess,
    feedbackRequestsQuery.data,
    feedbackRequestsQuery.isSuccess,
    members,
    period.starts,
    praiseQuery.data,
    praiseQuery.isSuccess,
  ]);
  const previousFeedback = useMemo(() => {
    if (!previousFeedbackQuery.isSuccess || !previousPraiseQuery.isSuccess) {
      return [];
    }
    return buildFeedbackSignals({
      response: previousFeedbackQuery.data,
      requests: [],
      publicPraise: previousPraiseQuery.data,
      members,
      periodStart: previousPeriod.starts,
    });
  }, [
    members,
    previousFeedbackQuery.data,
    previousFeedbackQuery.isSuccess,
    previousPeriod.starts,
    previousPraiseQuery.data,
    previousPraiseQuery.isSuccess,
  ]);
  const programs = useMemo(
    () =>
      buildProgramSignals({
        submissionsResponse: checkinsQuery.data,
        cyclesResponse: cyclesQuery.data,
        memberIds,
        workspaceTimezone: auth.user.workspace.timezone,
        allowUnscopedAnonymous: !isAdmin || scope.type === 'workspace',
      }),
    [
      auth.user.workspace.timezone,
      checkinsQuery.data,
      cyclesQuery.data,
      memberIds,
      isAdmin,
      scope.type,
    ],
  );
  const model = useMemo(
    () => buildManagerDashboardModel({members, goals, feedback, programs}),
    [feedback, goals, members, programs],
  );

  const availability: Record<
    'goals' | 'feedback' | 'programs',
    FeatureAvailability
  > = {
    goals: {
      enabled: isGoalEnabled,
      loading: goalsQuery.isLoading,
      failed: goalsQuery.isError,
    },
    feedback: {
      enabled: isFeedBackEnabled,
      loading:
        feedbackQuery.isLoading ||
        feedbackRequestsQuery.isLoading ||
        praiseQuery.isLoading,
      failed:
        feedbackQuery.isError ||
        feedbackRequestsQuery.isError ||
        praiseQuery.isError,
    },
    programs: {
      enabled: isCheckinsEnabled,
      loading: checkinsQuery.isLoading || cyclesQuery.isLoading,
      failed: checkinsQuery.isError || cyclesQuery.isError,
    },
  };

  const updateParam = (key: 'scope' | 'period' | 'program', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'scope') next.delete('program');
    navigate(`${location.pathname}?${next.toString()}${location.hash}`);
  };

  const refetchFailed = () => {
    if (usersQuery.isError) usersQuery.refetch();
    if (groupsQuery.isError) groupsQuery.refetch();
    if (groupMembershipsQuery.isError) groupMembershipsQuery.refetch();
    if (goalsQuery.isError) goalsQuery.refetch();
    if (previousGoalsQuery.isError) previousGoalsQuery.refetch();
    if (feedbackQuery.isError) feedbackQuery.refetch();
    if (feedbackRequestsQuery.isError) feedbackRequestsQuery.refetch();
    if (praiseQuery.isError) praiseQuery.refetch();
    if (previousFeedbackQuery.isError) previousFeedbackQuery.refetch();
    if (previousPraiseQuery.isError) previousPraiseQuery.refetch();
    if (checkinsQuery.isError) checkinsQuery.refetch();
    if (cyclesQuery.isError) cyclesQuery.refetch();
  };

  return {
    model,
    previousGoals,
    previousFeedback,
    scopes,
    scope,
    periodOptions,
    period,
    availability,
    selectedProgramId: searchParams.get('program') || programs[0]?.id || '',
    rosterLoading:
      usersQuery.isLoading ||
      groupsQuery.isLoading ||
      groupMembershipsQuery.isLoading,
    rosterFailed:
      usersQuery.isError ||
      groupsQuery.isError ||
      groupMembershipsQuery.isError,
    updateParam,
    refetchFailed,
  };
};
